import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Upload, Check, Palette, DraftingCompass } from "lucide-react";
import { toast } from "sonner";
import api from "@/api";
import { Reveal, MaskedLines, Overline, Magnetic } from "@/components/Motion";

const STATUSES = [
  { key: "student", label: "Student" },
  { key: "freelancer", label: "Freelancer" },
  { key: "professional", label: "Professional" },
];

const MEDIUMS = ["Pencil", "Acrylic", "Oil", "Murals / Wall Art", "Resin", "Digital Art", "Other"];

const SOFTWARES = ["AutoCAD", "SketchUp", "Revit", "Rhino", "Grasshopper", "Blender", "V-Ray", "Enscape", "Lumion", "Other"];

const inputCls = "w-full bg-transparent border-b border-ink/25 focus:border-brand outline-none py-3 text-base placeholder:text-ink/30 transition-colors";

function Chip({ active, onClick, testid, children }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] border transition-colors ${
        active ? "bg-ink text-paper border-ink" : "border-ink/20 text-ink/60 hover:border-brand hover:text-brand"
      }`}
    >
      {children}
    </button>
  );
}

function PortfolioFileField({ file, onPick, inputRef }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/40 mb-2">Portfolio file — PDF or image, max 10 MB</p>
      <button
        type="button"
        data-testid="com-portfolio"
        onClick={() => inputRef.current.click()}
        className={`w-full border border-dashed px-5 py-6 text-left transition-colors flex items-center gap-4 ${
          file ? "border-brand/60 bg-brand/5" : "border-ink/25 hover:border-brand"
        }`}
      >
        {file ? <Check size={16} className="text-brand shrink-0" /> : <Upload size={16} className="text-ink/40 shrink-0" />}
        <span className={`text-sm truncate ${file ? "text-ink" : "text-ink/40"}`}>
          {file ? file.name : "Choose PDF or image"}
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/jpeg,image/png,image/webp"
        className="hidden"
        data-testid="com-portfolio-input"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          const ok = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
          if (!ok.includes(f.type)) {
            toast.error("Portfolio must be a PDF or image (jpg, png, webp).");
            e.target.value = "";
            return;
          }
          if (f.size > 10 * 1024 * 1024) {
            toast.error("Please keep the file under 10 MB.");
            e.target.value = "";
            return;
          }
          onPick(f);
        }}
      />
    </div>
  );
}

export default function Community() {
  const [type, setType] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", whatsapp: "", city: "", experience: "",
    course: "", study_year: "", portfolio_link: "", other_medium: "",
    services_offered: "", other_software: "",
  });
  const [status, setStatus] = useState("freelancer");
  const [mediums, setMediums] = useState([]);
  const [software, setSoftware] = useState({}); // name -> proficiency
  const [portfolio, setPortfolio] = useState(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const portfolioRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleMedium = (m) => setMediums((p) => (p.includes(m) ? p.filter((x) => x !== m) : [...p, m]));
  const toggleSoftware = (s) =>
    setSoftware((p) => {
      const next = { ...p };
      if (s in next) delete next[s];
      else next[s] = 50;
      return next;
    });

  const pick = (t) => {
    setType(t);
    setMediums([]);
    setSoftware({});
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!portfolio && !form.portfolio_link.trim()) {
      toast.error("Please upload a portfolio file or paste a portfolio link.");
      return;
    }
    if (!consent) {
      toast.error("Please give your consent so we can store and respond to your application.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("community_type", type);
      fd.append("status", status);
      fd.append("consent", "true");
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (type === "art") {
        fd.set("mediums", mediums.filter((m) => m !== "Other").join(", "));
      } else {
        fd.set("software", JSON.stringify(
          Object.entries(software).filter(([n]) => n !== "Other").map(([name, proficiency]) => ({ name, proficiency }))
        ));
      }
      if (portfolio) fd.append("portfolio", portfolio);
      await api.post("/community/apply", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your application. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="community-page" className="pt-32 pb-24 px-6 md:px-12 max-w-7xl mx-auto min-h-screen">
      <Overline>Community</Overline>
      <MaskedLines
        className="mt-6"
        lineClassName="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight"
        lines={["Join the IKSHA", "Freelance Community"]}
      />
      <Reveal className="mt-8 max-w-2xl">
        <p className="text-ink/60 leading-relaxed" data-testid="community-subtitle">
          Share freelance opportunities, find collaborators, and create together.
        </p>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl" data-testid="community-options">
        {[
          { key: "art", icon: Palette, title: "Art Community", cta: "Join as an Artist" },
          { key: "architecture", icon: DraftingCompass, title: "Architecture & Design Community", cta: "Join as an Architect or Designer" },
        ].map((o) => (
          <button
            key={o.key}
            type="button"
            data-testid={`community-type-${o.key}`}
            onClick={() => pick(o.key)}
            className={`border p-8 text-left transition-colors group ${
              type === o.key ? "border-brand bg-brand/5" : "border-ink/15 hover:border-brand"
            }`}
          >
            <o.icon size={22} strokeWidth={1.5} className={type === o.key ? "text-brand" : "text-ink/40 group-hover:text-brand transition-colors"} />
            <h2 className="font-display text-2xl tracking-tight mt-5">{o.title}</h2>
            <p className={`mt-4 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] ${type === o.key ? "text-brand" : "text-ink/50"}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-brand inline-block" /> {o.cta}
            </p>
          </button>
        ))}
      </div>

      {done ? (
        <Reveal className="mt-16 border border-brand/40 bg-brand/5 p-10 text-center max-w-2xl">
          <span className="w-3 h-3 rounded-full bg-brand inline-block" />
          <h2 className="font-display text-3xl tracking-tight mt-5" data-testid="community-success">Application received.</h2>
          <p className="text-ink/60 mt-3 leading-relaxed">
            We&rsquo;ll review your portfolio and contact you by email.
          </p>
        </Reveal>
      ) : type ? (
        <Reveal className="mt-16 max-w-3xl">
          <form onSubmit={submit} data-testid="community-form" className="space-y-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <input required data-testid="com-name" value={form.name} onChange={set("name")} placeholder="Full name *" className={inputCls} />
              <input required type="email" data-testid="com-email" value={form.email} onChange={set("email")} placeholder="Email *" className={inputCls} />
              <input data-testid="com-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} placeholder="WhatsApp number" className={inputCls} />
              <input data-testid="com-city" value={form.city} onChange={set("city")} placeholder="City / address" className={inputCls} />
              <input data-testid="com-experience" value={form.experience} onChange={set("experience")} placeholder="Experience (e.g. 2 years freelance)" className={inputCls} />
            </div>

            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/40 mb-4">You are a</p>
              <div className="flex flex-wrap gap-2" data-testid="com-status">
                {STATUSES.map((s) => (
                  <Chip key={s.key} testid={`com-status-${s.key}`} active={status === s.key} onClick={() => setStatus(s.key)}>{s.label}</Chip>
                ))}
              </div>
              {status === "student" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
                  <input required data-testid="com-course" value={form.course} onChange={set("course")} placeholder="Course (e.g. B.Arch, MFA) *" className={inputCls} />
                  <input required data-testid="com-study-year" value={form.study_year} onChange={set("study_year")} placeholder="Year of study (e.g. 3rd year) *" className={inputCls} />
                </div>
              )}
            </div>

            {type === "art" ? (
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/40 mb-4">Mediums you work in — select all that apply</p>
                <div className="flex flex-wrap gap-2" data-testid="com-mediums">
                  {MEDIUMS.map((m) => (
                    <Chip key={m} testid={`medium-${m.toLowerCase().replace(/[^a-z]+/g, "-")}`} active={mediums.includes(m)} onClick={() => toggleMedium(m)}>{m}</Chip>
                  ))}
                </div>
                {mediums.includes("Other") && (
                  <input data-testid="com-other-medium" value={form.other_medium} onChange={set("other_medium")}
                    placeholder="Tell us your other medium" className={`${inputCls} mt-5`} />
                )}
              </div>
            ) : (
              <div className="space-y-10">
                <input data-testid="com-services" value={form.services_offered} onChange={set("services_offered")}
                  placeholder="Services you offer (e.g. drafting, 3D visualisation, site documentation)" className={inputCls} />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/40 mb-4">Software — select and rate your proficiency</p>
                  <div className="flex flex-wrap gap-2" data-testid="com-software">
                    {SOFTWARES.map((s) => (
                      <Chip key={s} testid={`software-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`} active={s in software} onClick={() => toggleSoftware(s)}>{s}</Chip>
                    ))}
                  </div>
                  {Object.keys(software).filter((s) => s !== "Other").length > 0 && (
                    <div className="mt-6 space-y-4" data-testid="com-proficiencies">
                      {Object.keys(software).filter((s) => s !== "Other").map((s) => (
                        <div key={s} className="flex items-center gap-4">
                          <span className="w-32 shrink-0 font-mono text-[10px] uppercase tracking-[0.15em] text-ink/60">{s}</span>
                          <input
                            type="range" min="10" max="100" step="5"
                            value={software[s]}
                            onChange={(e) => setSoftware((p) => ({ ...p, [s]: Number(e.target.value) }))}
                            className="flex-1 accent-[#E53827]"
                            data-testid={`proficiency-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          />
                          <span className="w-12 text-right font-mono text-xs text-ink/70">{software[s]}%</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {"Other" in software && (
                    <input data-testid="com-other-software" value={form.other_software} onChange={set("other_software")}
                      placeholder="Other software you use" className={`${inputCls} mt-5`} />
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
              <PortfolioFileField file={portfolio} onPick={setPortfolio} inputRef={portfolioRef} />
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/40 mb-2">Or a portfolio link</p>
                <input data-testid="com-portfolio-link" value={form.portfolio_link} onChange={set("portfolio_link")}
                  placeholder="https://… (Behance, Drive, Instagram)" className={inputCls} />
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer max-w-xl" data-testid="com-consent-label">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="w-4 h-4 mt-0.5 accent-[#E53827] shrink-0"
                data-testid="com-consent"
              />
              <span className="text-sm text-ink/60 leading-relaxed">
                I consent to IKSHA storing my application and contacting me by email. *
              </span>
            </label>

            <div className="flex flex-wrap items-center gap-6">
              <Magnetic>
                <button type="submit" disabled={busy} data-testid="com-submit-btn"
                  className="bg-ink text-paper px-10 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-brand transition-colors disabled:opacity-50">
                  {busy ? "Sending…" : "Submit Application"}
                </button>
              </Magnetic>
              <p className="text-sm text-ink/50 max-w-sm" data-testid="community-note">
                For freelance collaboration. For permanent roles, use our{" "}
                <Link to="/careers" className="text-brand underline">job application form</Link>.
              </p>
            </div>
          </form>
        </Reveal>
      ) : (
        <p className="mt-16 font-mono text-xs uppercase tracking-[0.2em] text-ink/40" data-testid="community-pick-hint">
          Choose a community above to continue
        </p>
      )}
    </main>
  );
}
